/**
 * Versioned postMessage protocol for @callcorpacd/platform-bridge.
 *
 * PROTOCOL_VERSION — latest wire version this package speaks.
 * MIN_PROTOCOL_VERSION — oldest guest envelope version the host still accepts.
 * Prefer additive changes on the current version; bump PROTOCOL_VERSION only for
 * incompatible shape/semantic breaks. Raise MIN only when deliberately forcing upgrades.
 */

export const PROTOCOL_VERSION = 4;
/** Oldest protocol version the host will negotiate with. */
export const MIN_PROTOCOL_VERSION = 4;
export const CHANNEL = 'callcorp.platform-bridge';

export const MessageType = Object.freeze({
    Ready: 'bridge.ready',
    ReadyAck: 'bridge.ready.ack',
    /** Host → guest: handshake rejected (e.g. version outside supported range) */
    ReadyReject: 'bridge.ready.reject',
    ApiRequest: 'bridge.api.request',
    ApiResponse: 'bridge.api.response',
    ApiError: 'bridge.api.error',
    /** Host → guest: refreshed ControlData.Params map and/or ControlData.Route string */
    ContextUpdate: 'bridge.context.update',

    /** Guest → host: open a realtime feed subscription */
    RealtimeOpen: 'bridge.realtime.open',
    /** Host → guest: open result `{ subscriptionId, snapshot }` */
    RealtimeOpenResult: 'bridge.realtime.open.result',
    /** Guest → host: update list query (same subscription id) */
    RealtimeSetQuery: 'bridge.realtime.setQuery',
    /** Host → guest: setQuery result `{ snapshot }` */
    RealtimeSetQueryResult: 'bridge.realtime.setQuery.result',
    /** Guest → host: close subscription */
    RealtimeClose: 'bridge.realtime.close',
    /** Host → guest: close ack */
    RealtimeCloseResult: 'bridge.realtime.close.result',
    /** Host → guest: snapshot push `{ subscriptionId, snapshot, revision }` */
    RealtimeUpdate: 'bridge.realtime.update',
    /** Host → guest: connection/feed status `{ subscriptionId?, status, message? }` */
    RealtimeStatus: 'bridge.realtime.status',
    /** Host → guest: realtime RPC error `{ message, code }` */
    RealtimeError: 'bridge.realtime.error',

    /** Guest → host: UI helper RPC `{ method, args }` */
    UiRequest: 'bridge.ui.request',
    /** Host → guest: UI helper result `{ result }` */
    UiResponse: 'bridge.ui.response',
    /** Host → guest: UI helper error `{ message, code }` */
    UiError: 'bridge.ui.error',

    /** Guest → host: events RPC `{ method, args }` (document actions, e.g. runEventActions) */
    EventsRequest: 'bridge.events.request',
    /** Host → guest: events result `{ result }` */
    EventsResponse: 'bridge.events.response',
    /** Host → guest: events error `{ message, code }` */
    EventsError: 'bridge.events.error',

    /** Host → guest: theme snapshot `{ colors, name }` (white-label / active portal theme) */
    ThemeUpdate: 'bridge.theme.update',

    /** Guest → host: subscribe to server/session events */
    ServerEventsSubscribe: 'bridge.serverEvents.subscribe',
    /** Host → guest: subscribe result `{ subscriptionId }` */
    ServerEventsSubscribeResult: 'bridge.serverEvents.subscribe.result',
    /** Guest → host: unsubscribe */
    ServerEventsUnsubscribe: 'bridge.serverEvents.unsubscribe',
    /** Host → guest: unsubscribe ack */
    ServerEventsUnsubscribeResult: 'bridge.serverEvents.unsubscribe.result',
    /** Host → guest: server event push `{ subscriptionId, event }` */
    ServerEventsEvent: 'bridge.serverEvents.event',
    /** Host → guest: server-events RPC error `{ message, code }` */
    ServerEventsError: 'bridge.serverEvents.error',

    /** Guest → host: subscribe to portal EventBus channel */
    BusSubscribe: 'bridge.bus.subscribe',
    /** Host → guest: subscribe result `{ subscriptionId }` */
    BusSubscribeResult: 'bridge.bus.subscribe.result',
    /** Guest → host: unsubscribe */
    BusUnsubscribe: 'bridge.bus.unsubscribe',
    /** Host → guest: unsubscribe ack */
    BusUnsubscribeResult: 'bridge.bus.unsubscribe.result',
    /** Host → guest: bus event push `{ subscriptionId, payload }` */
    BusEvent: 'bridge.bus.event',
    /** Host → guest: bus RPC error `{ message, code }` */
    BusError: 'bridge.bus.error',
});

export const ApiMethod = Object.freeze({
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE',
    CACHED_GET: 'CACHED_GET',
});

/** Feed kinds matching RTEventHandler widget views vs named lists */
export const RealtimeFeedKind = Object.freeze({
    Widget: 'widget',
    List: 'list',
});

/**
 * @param {unknown} version
 * @returns {boolean}
 */
export function isSupportedProtocolVersion(version) {
    return typeof version === 'number'
        && Number.isFinite(version)
        && version >= MIN_PROTOCOL_VERSION
        && version <= PROTOCOL_VERSION;
}

/**
 * @param {unknown} data
 * @returns {boolean}
 */
export function isBridgeMessage(data) {
    return !!(
        data &&
        typeof data === 'object' &&
        data.channel === CHANNEL &&
        typeof data.version === 'number' &&
        typeof data.type === 'string'
    );
}

/**
 * @param {object} options
 * @param {string} options.type
 * @param {string} [options.requestId]
 * @param {unknown} [options.payload]
 * @param {number} [options.version=PROTOCOL_VERSION]
 * @returns {{ channel: string, version: number, type: string, requestId?: string, payload?: unknown }}
 */
export function createMessage({ type, requestId, payload, version = PROTOCOL_VERSION }) {
    const message = {
        channel: CHANNEL,
        version,
        type,
    };
    if (requestId !== undefined)
        message.requestId = requestId;
    if (payload !== undefined)
        message.payload = payload;
    return message;
}
